import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { flashSalesAPI, enabledServicesAPI } from '../../services/api';

const getInitialState = () => ({
  sales: [],
  pagination: null,
  tabCounts: { Ongoing: 0, Past: 0 },
  serviceOptions: [],
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isLoadingServices: false,
  error: null,
});

// ==================== GET FLASH SALES ====================
export const getFlashSales = createAsyncThunk(
  'flashSales/getFlashSales',
  async (params, { rejectWithValue }) => {
    try {
      const response = await flashSalesAPI.getFlashSales(params);
      console.log("response.data---flash sales", response.data)
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error || error.response?.data?.message || 'Failed to fetch flash sales.'
      );
    }
  }
);

// ==================== CREATE FLASH SALE ====================
export const createFlashSale = createAsyncThunk(
  'flashSales/createFlashSale',
  async (data, { rejectWithValue }) => {
    try {
      const response = await flashSalesAPI.createFlashSale(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error || error.response?.data?.message || 'Failed to create flash sale.'
      );
    }
  }
);

// ==================== UPDATE FLASH SALE (edit / pause / resume / restart) ====================
export const updateFlashSale = createAsyncThunk(
  'flashSales/updateFlashSale',
  async ({ flashId, data }, { rejectWithValue }) => {
    try {
      const response = await flashSalesAPI.updateFlashSale(flashId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error || error.response?.data?.message || 'Failed to update flash sale.'
      );
    }
  }
);

// ==================== DELETE FLASH SALE ====================
export const deleteFlashSale = createAsyncThunk(
  'flashSales/deleteFlashSale',
  async (flashId, { rejectWithValue }) => {
    try {
      const response = await flashSalesAPI.deleteFlashSale(flashId);
      return { flashId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error || error.response?.data?.message || 'Failed to delete flash sale.'
      );
    }
  }
);

// ==================== GET ENABLED SERVICES ====================
export const getEnabledServices = createAsyncThunk(
  'flashSales/getEnabledServices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await enabledServicesAPI.getEnabledServices();
      console.log("response.data---services", response.data)
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error || error.response?.data?.message || 'Failed to fetch enabled services.'
      );
    }
  }
);

// ==================== SLICE ====================
const flashSalesSlice = createSlice({
  name: 'flashSales',
  initialState: getInitialState(),
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {

    // GET FLASH SALES
    builder
      .addCase(getFlashSales.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getFlashSales.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sales = action.payload.Sales || [];
        state.pagination = action.payload.Pagination || null;
        state.tabCounts = action.payload.TabCounts || { Ongoing: 0, Past: 0 };
        state.error = null;
      })
      .addCase(getFlashSales.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // CREATE FLASH SALE
    builder
      .addCase(createFlashSale.pending, (state) => { state.isCreating = true; state.error = null; })
      .addCase(createFlashSale.fulfilled, (state) => { state.isCreating = false; })
      .addCase(createFlashSale.rejected, (state, action) => { state.isCreating = false; state.error = action.payload; });

    // UPDATE FLASH SALE
    builder
      .addCase(updateFlashSale.pending, (state) => { state.isUpdating = true; state.error = null; })
      .addCase(updateFlashSale.fulfilled, (state) => { state.isUpdating = false; })
      .addCase(updateFlashSale.rejected, (state, action) => { state.isUpdating = false; state.error = action.payload; });

    // DELETE FLASH SALE
    builder
      .addCase(deleteFlashSale.pending, (state) => { state.isUpdating = true; })
      .addCase(deleteFlashSale.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.sales = state.sales.filter(s => s.FlashId !== action.payload.flashId);
      })
      .addCase(deleteFlashSale.rejected, (state, action) => { state.isUpdating = false; state.error = action.payload; });

    // GET ENABLED SERVICES
    builder
      .addCase(getEnabledServices.pending, (state) => { state.isLoadingServices = true; })
      .addCase(getEnabledServices.fulfilled, (state, action) => {
        state.isLoadingServices = false;
        state.serviceOptions = action.payload.EnabledServices || [];
      })
      .addCase(getEnabledServices.rejected, (state, action) => {
        state.isLoadingServices = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = flashSalesSlice.actions;
export default flashSalesSlice.reducer;